import cookies from './../../cookies.js';
import Mustache from 'mustache';
import {removeFromShoppingCart, getCart} from './shopping-cart-utils.js';
import analytics from '../../analytics.js';
import LoadingIcon from '../../generic/loading-icon.js';
import CarPageApp from '../../autosivu/2021/car-page-app.js';

/**
 * Shopping cart module for handling UI components event, rendering and data fetching.
 */
class ShoppingCart {

  /**
   * Is shopping cart in loading state
   *
   * @type {boolean}
   */
  #loading = false;

  /**
   * @type {object} - DOM elements
   */
  #dom = {};

  /**
   * Data object for rendering items in the shopping cart.
   */
  #data = null;

  /**
   * @constructor
   * @param {HTMLElement} element - Element to bind to
   */
  constructor(element) {
    this.#dom.element = element;
    this.findElements();
    this.addEventListeners();
    this.loading = true;
  }

  /**
   * Find elements from DOM
   */
  findElements() {
    this.#dom.articles = this.#dom.element.querySelector('.articles');
    this.#dom.articleTemplate = this.#dom.element.querySelector('.mustache-cart-article-template');
    this.#dom.analyticsTemplate = this.#dom.element.querySelector('.mustache-analytics');
  }

  /**
   * Attach event listeners
   */
  addEventListeners() {
    this.#dom.element.addEventListener('click', async event => {
      const {target} = event;
      if( target.tagName === 'BUTTON' && target.classList.contains('remove-button') ) {
        await this.#onRemoveFromShoppingCart(event);
        this.#render();
      }
      if( target.tagName === 'BUTTON' && target.classList.contains('buy-button') ) {
        this.#onBuyButtonPressed(event);
      }

      if( target.tagName === 'BUTTON' && target.classList.contains('reserve-button') ) {
        this.#onReserveButtonPressed(event);
      }
    });
  }

  /**
   * On buy button is pressed
   * @param {Event} event click event
   */
  #onBuyButtonPressed(event) {
    event.preventDefault();
    const itemId = event.target.closest('.cart-card').dataset.id;

    // Send analytics
    const itemData = this.#data.items.find(item => item.id === itemId);
    const googleAnalytics = itemData.buyButton.googleAnalytics;
    analytics.pushUIInteractionAnalytics({
      data: googleAnalytics.interaction,
    });
  }

  /**
   * On reserve button is pressed
   * @param {Event} event click event
   */
  #onReserveButtonPressed(event) {
    event.preventDefault();
    const itemId = event.target.closest('.cart-card').dataset.id;

    // Send analytics
    const itemData = this.#data.items.find(item => item.id === itemId);
    const googleAnalytics = itemData.reserveButton.googleAnalytics;
    analytics.pushUIInteractionAnalytics({
      data: googleAnalytics.interaction,
    });
  }

  /**
   * Send analytic event to bit space!
   * @param {string} itemId item's id license, pid or somethings similar
   */
  async sendCheckoutAnalyticEvent( itemId ) {
    try {
      const urlSearchParams = new URLSearchParams();

      this.#data.items.forEach(vehicle => {
        urlSearchParams.append('license[]', vehicle.id);
      });

      const response = await fetch('/stage-ajax/car-data' + '?' + urlSearchParams.toString());
      const vehicles = await response.json();

      console.log( vehicles )

      analytics.pushCheckout(this.#data.id, vehicles);
    } catch( error ) {
      console.log('Error fetching car data', error);
    }
  }

  /**
   * Handle remove from shopping cart event
   * @param {object} event event object
   */
  async #onRemoveFromShoppingCart(event) {
    this.loading = true;
    const itemId = event.target.dataset.id;

    // Send analytics
    const itemData = this.#data.items.find(item => item.id.toString() === itemId);
    const googleAnalytics = itemData.removeButtonAnalytics.googleAnalytics;
    analytics.pushUIInteractionAnalytics({
      data: googleAnalytics.interaction,
    });

    this.#data = await removeFromShoppingCart(itemId);

    // Update car page app state
    const carPageApp = new CarPageApp(event.target.dataset.id);
    carPageApp.updateState('cart.isInCart', 0);
    carPageApp.updateState('dialogs.addCartDialog', 0);
    carPageApp.storeState();

    this.loading = false;
  }

  /**
   * Fetch data from API
   */
  async fetchData() {
    this.loading = true;
    this.#data = await getCart(cookies.getCookie('cart'));
    this.loading = false;
    console.log('Shopping cart data', this.#data);
  }

  /**
   * Set loading state, if loading set loader
   *
   * @param {boolean} value - True if loading, otherwise false
   */
  set loading(value) {
    this.#loading = value;
    LoadingIcon.instances['shoppingcart-loading-icon'].show = value;
  }

  /**
   * Get shopping cart data from API
   */
  #render() {
    const articleTemplate = this.#dom.articleTemplate?.innerText;
    const analyticsTemplate = this.#dom.analyticsTemplate?.innerText;

    const html = Mustache.render('{{^items.length}}<h1 class="text-center">Ostoskori on tyhjä</h1>{{/items.length}}{{#items}}{{>articleTemplate}}{{/items}}', { ...this.#data}, {articleTemplate, analytics: analyticsTemplate});
    this.#dom.articles.innerHTML = html;
  }

  static async init() {
    const element = document.querySelector('.shopping-cart');
    if( element ) {
      const shoppingCart = new ShoppingCart(element);
      await shoppingCart.fetchData();
      shoppingCart.#render();
      shoppingCart.sendCheckoutAnalyticEvent();
    }
  }
}

export default ShoppingCart;


